import React from 'react';

/**
 * RadialLayout Component
 * Positions the first child in the center and distributes remaining children in a circle or arc around it.
 * 
 * @param {Object} props Component props
 * @param {React.ReactNode} props.children Children elements to be arranged
 * @param {number} props.radius The radius of the circle (in pixels)
 * @param {number} props.startAngle The starting angle in degrees (0 = top, 90 = right, etc.)
 * @param {number} props.endAngle The ending angle in degrees (determines the arc of distribution)
 * @param {Object} props.style Additional styles for the container
 */
export const RadialLayout = ({ 
  children, 
  radius = 150, 
  startAngle = 0,
  endAngle = startAngle + 360, // Default to full circle
  style = {} 
}) => {
  // Ensure children is always an array
  const childrenArray = React.Children.toArray(children);
  
  // Need at least one child
  if (childrenArray.length === 0) {
    return null;
  }
  
  // Extract the first child (center) and the rest (to be arranged radially)
  const centerChild = childrenArray[0];
  const radialChildren = childrenArray.slice(1);
  
  // Calculate the total angle span
  let angleSpan = endAngle - startAngle;
  // Normalize to positive value (handle cases where endAngle < startAngle)
  angleSpan = (angleSpan + 360) % 360 || 360; // If 0, use full circle (360)
  
  // Calculate the angle step for evenly distributing the elements
  const angleStep = radialChildren.length <= 1 ? 0 : angleSpan / (radialChildren.length - 1);
  
  return (
    <div 
      style={{
        position: 'relative',
        //width: `${radius * 2 + 100}px`, // Add some extra space for elements
        height: `${radius * 2 + 100}px`,
        ...style
      }}
    >
      {/* Center element */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2
        }}
      >
        {centerChild}
      </div>
      
      {/* Radial elements */}
      {radialChildren.map((child, index) => {
        // For single radial child, place at startAngle
        // Otherwise, distribute evenly between startAngle and endAngle
        const angle = radialChildren.length === 1 
          ? startAngle 
          : startAngle + (index * angleStep);
        const radians = (angle * Math.PI) / 180;
        
        // Calculate x and y positions
        const x = radius * Math.sin(radians);
        const y = -radius * Math.cos(radians); // Negative because Y grows downward in CSS
        
        return (
          <div
            key={index}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
            }}
          >
            {child}
          </div>
        );
      })}
    </div>
  );
};

export default RadialLayout;